import { useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { OperatorErrorText } from '../renderer/TechnicalDetails';
import './content-central-sheet.css';
import type { PaigeDestination } from './paige.global';

/** CapturePoint's line for the sends Paige could not confirm. */
export function unconfirmedLine(count: number, product: string): string {
  return count === 1
    ? `1 document may already be in ${product}: Paige could not tell. Look for it before sending it again.`
    : `${count} documents may already be in ${product}: Paige could not tell. Look for them before sending them again.`;
}

/**
 * Under a destination's row, for sends that may have landed (parity E-41): Paige never sends them again by
 * itself, and Retry leaves them alone, because a second send would file a second copy. A person looks at the
 * destination and, not finding them, presses "Not there: send again": each goes once more, to this destination
 * alone; the press is recorded.
 */
export function SendAgainOffer({ row }: { row: PaigeDestination }) {
  const bridge = window.paigeWorkbench?.destinations;
  const [line, setLine] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const count = row.unconfirmed ?? 0;
  if (line) return <small className="paige-destination-backfill">{line}</small>;
  if (!bridge?.sendAgain || count === 0) return null;
  const send = async () => {
    setBusy(true);
    try {
      const queued = await bridge.sendAgain!(row.id);
      setLine(
        queued === 1
          ? '1 document is on its way again.'
          : `${queued} documents are on their way again.`,
      );
    } catch (failure) {
      setLine(failure instanceof Error ? failure.message : 'Paige could not send them again');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="paige-destination-unconfirmed" role="alert">
      <TriangleAlert size={12} aria-hidden="true" />
      <OperatorErrorText
        message={unconfirmedLine(count, row.kind === 'nucleus-one' ? 'Nucleus One' : row.label)}
      />
      <button
        type="button"
        className="secondary-button small"
        disabled={busy}
        onClick={() => void send()}
      >
        Not there: send again
      </button>
    </span>
  );
}
